import React, { createContext, useContext, useState, useRef, useCallback, useMemo } from 'react';
import { logger } from '../utils/logger';
import { trackEvent } from '../utils/analytics';
import { useConnectionSpeed } from '../hooks/useConnectionSpeed';

const CanvasContext = createContext();

export const useCanvasContext = () => {
  const context = useContext(CanvasContext);
  if (!context) {
    throw new Error('useCanvasContext must be used within a CanvasProvider');
  }
  return context;
};

export const CanvasProvider = ({ children }) => {
  // Detect connection speed and determine asset quality (applies to models, videos, etc.)
  const { quality: assetQuality, effectiveType } = useConnectionSpeed();
  
  // Legacy alias for backward compatibility (deprecated, use assetQuality)
  const modelQuality = assetQuality;
  
  const [models, setModels] = useState({});
  const [sceneObjects, setSceneObjects] = useState({});
  const canvasRef = useRef();
  const canvasElementRef = useRef(null); // Reference to the actual DOM canvas element
  const cameraRef = useRef();
  const phoneboxAudioRef = useRef(null); // Reference to the phonebox audio object (shared for phonebox.mp3 and hangup.mp3)
  
  // Phonebox state management
  const [phoneboxState, setPhoneboxState] = useState({
    isOffHook: false,
    isPlaying: false,
    audioContext: null,
    analyser: null,
    audioData: null,
    animationState: 'idle', // 'idle', 'pickup', 'hold', 'hangup'
    isInHoldPosition: false,
    // Question sequence state
    isQuestionDebugEnabled: true,
    isInQuestionMode: false,
    questionState: null,
    // Global lock to prevent multiple question sequences
    isQuestionSequenceActive: false,
    questionSequenceLockId: null
  });

  // Newspaper modal state
  const [newspaperModalOpen, setNewspaperModalOpen] = useState(false);

  // TV zoom state management
  const [tvState, setTvState] = useState({
    isZoomed: false
  });

  // Loading state management
  const [modelsLoaded, setModelsLoaded] = useState({
    phonebox: false,
    tv: false,
    news: false
  });

  // Promise-based model loading
  const [modelPromises, setModelPromises] = useState({
    phonebox: null,
    tv: null,
    news: null
  });

  // Signal for when preloader is ready to complete (models can position to final locations)
  const [readyToPosition, setReadyToPosition] = useState(false);


  // Register a model in the global context
  const registerModel = useCallback((name, modelData) => {
    setModels(prev => ({
      ...prev,
      [name]: {
        ...modelData,
        loaded: true,
        timestamp: Date.now()
      }
    }));
  }, []);

  // Get a registered model
  const getModel = useCallback((name) => {
    return models[name] || null;
  }, [models]);

  // Register scene objects (like phone groups, etc.)
  const registerSceneObject = useCallback((name, object) => {
    setSceneObjects(prev => ({
      ...prev,
      [name]: object
    }));
  }, []);

  // Get a scene object
  const getSceneObject = useCallback((name) => {
    return sceneObjects[name] || null;
  }, [sceneObjects]);

  // Clear a specific model (useful for cleanup)
  const clearModel = useCallback((name) => {
    setModels(prev => {
      const newModels = { ...prev };
      delete newModels[name];
      return newModels;
    });
  }, []);

  // Clear a specific scene object
  const clearSceneObject = useCallback((name) => {
    setSceneObjects(prev => {
      const newSceneObjects = { ...prev };
      delete newSceneObjects[name];
      return newSceneObjects;
    });
  }, []);

  // Phonebox control functions - memoized to prevent recreation
  const setPhoneOffHook = useCallback((isOffHook, metadata = {}) => {
    logger.debug('📞 setPhoneOffHook called with:', isOffHook);
    setPhoneboxState(prev => ({ ...prev, isOffHook }));

    const { source = 'unknown', reason } = metadata;
    trackEvent('phone_interaction', {
      event_category: 'engagement',
      event_label: isOffHook ? 'off_hook' : 'on_hook',
      interaction_source: source,
      reason
    });
    
    // Request microphone access when phone is taken off hook
    if (isOffHook) {
      logger.debug('📞 Phone taken off hook - requesting microphone access');
      
      // Dispatch a custom event that the microphone context can listen to
      const event = new CustomEvent('requestMicrophoneAccess', {
        detail: { reason: 'phoneOffHook' }
      });
      logger.debug('📞 Dispatching microphone request event:', event);
      window.dispatchEvent(event);
      
      // ANDROID: Dispatch event for speech recognition to start immediately (preserves user gesture)
      // This must be done synchronously from the click handler to preserve gesture context
      const speechRecognitionEvent = new CustomEvent('androidStartSpeechRecognition', {
        detail: { reason: 'phoneOffHook', timestamp: Date.now() }
      });
      logger.debug('📞 Dispatching Android speech recognition start event:', speechRecognitionEvent);
      window.dispatchEvent(speechRecognitionEvent);
    } else {
      // Phone hung up - clear any persistent session data to prevent cross-session contamination
      logger.debug('📞 Phone hung up - clearing session data');
      sessionStorage.removeItem('questionSequenceSubmissionId');
      
      // Notify microphone context to immediately stop any active streams
      const releaseEvent = new CustomEvent('releaseMicrophoneAccess', {
        detail: { reason: 'phoneOnHook' }
      });
      logger.debug('📞 Dispatching microphone release event:', releaseEvent);
      window.dispatchEvent(releaseEvent);
      
      // ANDROID: Dispatch event to stop speech recognition
      const stopSpeechRecognitionEvent = new CustomEvent('androidStopSpeechRecognition', {
        detail: { reason: 'phoneOnHook', timestamp: Date.now() }
      });
      logger.debug('📞 Dispatching Android speech recognition stop event:', stopSpeechRecognitionEvent);
      window.dispatchEvent(stopSpeechRecognitionEvent);
    }
  }, []);

  const setPhonePlaying = useCallback((isPlaying) => {
    setPhoneboxState(prev => ({ ...prev, isPlaying }));
  }, []);

  const setAudioContext = useCallback((audioContext, analyser) => {
    setPhoneboxState(prev => ({ 
      ...prev, 
      audioContext, 
      analyser 
    }));
  }, []);

  const setAudioData = useCallback((audioData) => {
    setPhoneboxState(prev => ({ ...prev, audioData }));
  }, []);

  const setAnimationState = useCallback((animationState) => {
    setPhoneboxState(prev => ({ ...prev, animationState }));
  }, []);

  const setIsInHoldPosition = useCallback((isInHoldPosition) => {
    setPhoneboxState(prev => ({ ...prev, isInHoldPosition }));
  }, []);

  const setIsQuestionDebugEnabled = useCallback((isQuestionDebugEnabled) => {
    setPhoneboxState(prev => ({ ...prev, isQuestionDebugEnabled: isQuestionDebugEnabled }));
  }, []);

  const setIsInQuestionMode = useCallback((isInQuestionMode, metadata = {}) => {
    setPhoneboxState(prev => ({ ...prev, isInQuestionMode }));

    const { source = 'unknown' } = metadata;
    trackEvent('question_mode_change', {
      event_category: 'engagement',
      event_label: isInQuestionMode ? 'entered' : 'exited',
      interaction_source: source
    });
  }, []);

  const setQuestionState = useCallback((questionState) => {
    setPhoneboxState(prev => ({ ...prev, questionState }));
  }, []);


  // Global question sequence lock management
  const acquireQuestionSequenceLock = useCallback(() => {
    const lockId = `lock_${Date.now()}_${Math.random().toString(36).substring(2, 15)}`;
    logger.debug('🔒 Attempting to acquire question sequence lock:', lockId);
    
    setPhoneboxState(prev => {
      if (prev.isQuestionSequenceActive) {
        logger.warn('🔒 Question sequence already active, lock acquisition failed');
        return prev; // Don't change state if already active
      }
      
      logger.debug('🔒 Question sequence lock acquired:', lockId);
      return {
        ...prev,
        isQuestionSequenceActive: true,
        questionSequenceLockId: lockId
      };
    });
    
    return lockId;
  }, []);

  const releaseQuestionSequenceLock = useCallback((lockId) => {
    logger.debug('🔓 Attempting to release question sequence lock:', lockId);
    
    setPhoneboxState(prev => {
      // If lock is already released (null), silently return - this is fine if multiple components try to release
      if (prev.questionSequenceLockId === null) {
        logger.debug('🔓 Lock already released (null), skipping release');
        return prev;
      }
      
      // If lock ID doesn't match and lock is not null, warn about mismatch
      if (prev.questionSequenceLockId !== lockId) {
        logger.warn('🔓 Lock ID mismatch, cannot release lock', {
          expected: lockId,
          actual: prev.questionSequenceLockId
        });
        return prev; // Don't change state if lock ID doesn't match
      }
      
      logger.debug('🔓 Question sequence lock released:', lockId);
      return {
        ...prev,
        isQuestionSequenceActive: false,
        questionSequenceLockId: null
      };
    });
  }, []);

  const isQuestionSequenceLocked = useCallback(() => {
    return phoneboxState.isQuestionSequenceActive;
  }, [phoneboxState.isQuestionSequenceActive]);

  // Newspaper modal control functions
  const openNewspaperModal = useCallback(() => {
    setNewspaperModalOpen(true);
    trackEvent('newspaper_modal_open', {
      event_category: 'engagement'
    });
  }, []);

  const closeNewspaperModal = useCallback(() => {
    setNewspaperModalOpen(false);
    trackEvent('newspaper_modal_close', {
      event_category: 'engagement'
    });
  }, []);

  // TV zoom control functions
  const setTvZoomed = useCallback((isZoomed, metadata = {}) => {
    logger.debug('📺 setTvZoomed called with:', isZoomed);
    setTvState(prev => ({ ...prev, isZoomed }));

    const { source = 'unknown', reason } = metadata;
    trackEvent('tv_interaction', {
      event_category: 'engagement',
      event_label: isZoomed ? 'zoomed_in' : 'zoomed_out',
      interaction_source: source,
      reason
    });
  }, []);

  const resetPhoneboxState = useCallback(() => {
    setPhoneboxState(prev => {
      // Only reset if state has actually changed to prevent unnecessary resets
      if (prev.isOffHook || prev.isPlaying || prev.audioContext || prev.analyser || 
          prev.audioData || prev.animationState !== 'idle' || prev.isInHoldPosition ||
          prev.isQuestionDebugEnabled || prev.isInQuestionMode || prev.questionState ||
          prev.isQuestionSequenceActive || prev.questionSequenceLockId) {
        logger.debug('Phonebox state and cached models reset to idle');
        return {
          isOffHook: false,
          isPlaying: false,
          audioContext: null,
          analyser: null,
          audioData: null,
          animationState: 'idle',
          isInHoldPosition: false,
          isQuestionDebugEnabled: false,
          isInQuestionMode: false,
          questionState: null,
          isQuestionSequenceActive: false,
          questionSequenceLockId: null
        };
      }
      return prev;
    });
    
    // Only reset cached models if they exist
    setModels(prev => {
      if (Object.keys(prev).length > 0) {
        return {};
      }
      return prev;
    });
    
    setSceneObjects(prev => {
      if (Object.keys(prev).length > 0) {
        return {};
      }
      return prev;
    });
  }, []);

  // Loading state management functions

  // Create a promise for a model to be positioned
  const createModelPromise = useCallback((modelName) => {
    return new Promise((resolve) => {
      setModelPromises(prev => ({
        ...prev,
        [modelName]: { resolve, timestamp: Date.now() }
      }));
    });
  }, []);

  // Combined function: set model loaded state and resolve promise
  const setModelLoaded = useCallback((modelName, loaded) => {
    // Update the loaded state
    setModelsLoaded(prev => ({
      ...prev,
      [modelName]: loaded
    }));

    // If setting to loaded=true, also resolve the promise
    if (loaded) {
      setModelPromises(prev => {
        const promise = prev[modelName];
        if (promise && promise.resolve) {
          promise.resolve();
          return {
            ...prev,
            [modelName]: null
          };
        }
        return prev;
      });
    }
  }, []);

  // Signal that preloader is ready to complete
  const setReadyToPositionSignal = useCallback((ready) => {
    setReadyToPosition(ready);
  }, []);

  // Register phonebox audio ref (called from Phonebox3DScene)
  const registerPhoneboxAudio = useCallback((audioRef) => {
    phoneboxAudioRef.current = audioRef;
    logger.debug('Phonebox audio ref registered in CanvasContext');
  }, []);

  // Get phonebox audio ref (for use in QuestionSequence)
  const getPhoneboxAudio = useCallback(() => {
    return phoneboxAudioRef.current;
  }, []);

  const value = useMemo(() => ({
    models,
    sceneObjects,
    registerModel,
    getModel,
    registerSceneObject,
    getSceneObject,
    clearModel,
    clearSceneObject,
    canvasRef,
    canvasElementRef,
    cameraRef,
    // Asset quality based on connection speed (applies to models, videos, etc.)
    assetQuality,
    // Legacy alias for backward compatibility (deprecated, use assetQuality)
    modelQuality,
    effectiveType,
    // Phonebox state and controls
    phoneboxState,
    setPhoneOffHook,
    setPhonePlaying,
    setAudioContext,
    setAudioData,
    setAnimationState,
    setIsInHoldPosition,
    setIsQuestionDebugEnabled,
    setIsInQuestionMode,
    setQuestionState,
    resetPhoneboxState,
    // Question sequence lock management
    acquireQuestionSequenceLock,
    releaseQuestionSequenceLock,
    isQuestionSequenceLocked,
    // Newspaper modal state and controls
    newspaperModalOpen,
    openNewspaperModal,
    closeNewspaperModal,
    // TV state and controls
    tvState,
    setTvZoomed,
    // Loading state and controls
    modelsLoaded,
    setModelLoaded,
    modelPromises,
    createModelPromise,
    readyToPosition,
    setReadyToPositionSignal,
    // Phonebox audio ref management
    registerPhoneboxAudio,
    getPhoneboxAudio
  }), [
    models,
    sceneObjects,
    assetQuality,
    modelQuality, // Include for backward compatibility
    effectiveType,
    phoneboxState,
    newspaperModalOpen,
    tvState,
    modelsLoaded,
    modelPromises,
    readyToPosition,
    // Callback functions (already memoized with useCallback)
    registerModel,
    getModel,
    registerSceneObject,
    getSceneObject,
    clearModel,
    clearSceneObject,
    setPhoneOffHook,
    setPhonePlaying,
    setAudioContext,
    setAudioData,
    setAnimationState,
    setIsInHoldPosition,
    setIsQuestionDebugEnabled,
    setIsInQuestionMode,
    setQuestionState,
    resetPhoneboxState,
    acquireQuestionSequenceLock,
    releaseQuestionSequenceLock,
    isQuestionSequenceLocked,
    openNewspaperModal,
    closeNewspaperModal,
    setTvZoomed,
    setModelLoaded,
    createModelPromise,
    setReadyToPositionSignal,
    registerPhoneboxAudio,
    getPhoneboxAudio
  ]);

  return (
    <CanvasContext.Provider value={value}>
      {children}
    </CanvasContext.Provider>
  );
};

